import axios, { AxiosError } from 'axios';
import { apiRoute } from '../constants/api-constants';
import { StaysApiError } from './stays-client';
import type { PhoneWarning } from '../utils/stays-phone-links';

/**
 * LOBBERO STAYS — the message library and the composer (plan S8;
 * lobbero_be/src/stays/routes/messages.js). Organisation-wide templates in
 * English, Português and Español; the composer fills one for a stay on the
 * server and stores nothing. Every failure is a StaysApiError.
 */

export type MessageLanguage = 'en' | 'pt' | 'es';

export interface MessageTemplate {
  id: string;
  default_key: string | null;
  language: MessageLanguage;
  title: string;
  category: string;
  content: string;
  version: number;
  updated_at: string;
}

export interface MessageLibrary {
  templates: MessageTemplate[];
  deleted_defaults: number;
  placeholders: string[];
  sensitive: string[];
}

export type ComposeWarningCode =
  | 'SENSITIVE_WITHHELD'
  | 'VALUE_MISSING'
  | 'NO_CHECKIN_LINK'
  | 'CHECKIN_LINK_ROTATE'
  | 'NO_COUNTRY_CODE'
  | 'INVALID_PHONE';

export interface ComposeResult {
  text: string;
  language: MessageLanguage;
  warnings: { code: ComposeWarningCode; placeholder: string | null; message: string }[];
  phone: { digits: string | null; warning: PhoneWarning | null };
  links: { whatsapp: string; viber: string };
  checkin_link: { state: 'included' | 'none' | 'rotate_needed' };
}

const call = async <T>(p: Promise<{ data: T }>, fallback: string): Promise<T> => {
  try {
    const { data } = await p;

    return data;
  } catch (e) {
    const err = e as AxiosError<Record<string, unknown>>;
    if (err && err.response) throw new StaysApiError(err.response.status, err.response.data || null, fallback);
    throw new StaysApiError(0, null, fallback);
  }
};

const base = `${apiRoute}/stays/message-templates`;

export const listMessageTemplates = (language: MessageLanguage): Promise<MessageLibrary> =>
  call(axios.get(base, { params: { language } }), "Couldn't load the message templates.");

export const createMessageTemplate = (input: {
  language: MessageLanguage;
  title: string;
  category?: string;
  content: string;
}): Promise<{ template: MessageTemplate }> => call(axios.post(base, input), 'Could not save the template.');

export const updateMessageTemplate = (
  id: string,
  input: { title?: string; category?: string; content?: string; version: number }
): Promise<{ template: MessageTemplate }> =>
  call(axios.patch(`${base}/${encodeURIComponent(id)}`, input), 'Could not save the template.');

export const deleteMessageTemplate = (id: string): Promise<{ deleted: boolean; restorable: boolean }> =>
  call(axios.delete(`${base}/${encodeURIComponent(id)}`), 'Could not delete the template.');

export const restoreDefaultTemplates = (language?: MessageLanguage): Promise<{ restored: number }> =>
  call(axios.post(`${base}/restore-defaults`, language ? { language } : {}), 'Could not restore the templates.');

export const composeMessage = (
  reservationId: string,
  input: { template_id: string } | { content: string; language?: MessageLanguage }
): Promise<ComposeResult> =>
  call(
    axios.post(`${apiRoute}/stays/reservations/${encodeURIComponent(reservationId)}/compose`, input),
    'Could not prepare the message.'
  );
